import { lazy, type ComponentType } from "react";

/**
 * 청크 로드 실패 판별. 브라우저마다 문구가 달라 메시지로 본다 —
 * Chrome/Edge `Failed to fetch dynamically imported module`,
 * Firefox `error loading dynamically imported module`,
 * Safari `Importing a module script failed`,
 * Vite preload 헬퍼 `Unable to preload CSS for ...`
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

/** 리로드로 복구를 시도했다는 표시 — 실패가 이어질 때 무한 새로고침을 막는다 */
const RELOAD_MARK_KEY = "chunk-reload-attempted";

const hasReloadMark = () => {
  try {
    return sessionStorage.getItem(RELOAD_MARK_KEY) != null;
  } catch {
    // 스토리지가 막힌 환경 — 가드를 못 세우니 리로드를 아예 하지 않는다
    return true;
  }
};

const setReloadMark = () => {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, "1");
  } catch {
    // hasReloadMark 에서 이미 걸러지는 경로다
  }
};

const clearReloadMark = () => {
  try {
    sessionStorage.removeItem(RELOAD_MARK_KEY);
  } catch {
    // 위와 같다
  }
};

/**
 * 서버가 살아 있는지 확인한다. 배포 중 컨테이너 교체로 몇 초 끊긴 상태에서
 * 리로드하면 앱 에러 화면 대신 브라우저의 "연결할 수 없음" 화면으로 떨어진다.
 */
async function isServerReachable(): Promise<boolean> {
  try {
    const response = await fetch("/index.html", {
      method: "HEAD",
      cache: "no-store",
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * 라우트용 `lazy`. 청크 로드가 실패하면 세션당 한 번 새로고침해 스스로 복구한다.
 *
 * 배포로 청크 해시가 갈리면, 열어둔 탭이 들고 있는 옛 index.html 이 사라진 파일을
 * 요청해 라우트 이동이 실패한다. 같은 URL 을 다시 import 해도 모듈 맵에 실패가
 * 캐시돼 재요청되지 않으므로, 새 index.html 을 받는 리로드가 유일한 복구 수단이다.
 */
export function lazyRoute<T extends ComponentType>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const module = await load();
      // 다음 배포에서도 다시 복구할 수 있게 표시를 지운다
      clearReloadMark();
      return module;
    } catch (error) {
      if (!isChunkLoadError(error) || hasReloadMark()) throw error;
      if (!(await isServerReachable())) throw error;

      setReloadMark();
      window.location.reload();
      // 리로드가 시작될 때까지 에러 화면이 깜빡이지 않게 붙잡아 둔다
      return new Promise<never>(() => {});
    }
  });
}
